import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { portfolioData } from '../../data/portfolio';

const CertificationsContainer = styled.section`
  padding: 80px 20px;
  background: ${({ theme }) => theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 600;
  text-align: center;
  margin-bottom: 60px;
  color: ${({ theme }) => theme.colors.text};
`;

const CategorySection = styled.div`
  margin-bottom: 50px;
`;

const CategoryTitle = styled(motion.h3)`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.accent};
  border-bottom: 2px solid ${({ theme }) => theme.colors.accent};
  padding-bottom: 8px;
  display: inline-block;
`;

const CertificationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const CertificationCard = styled(motion.a)`
  display: block;
  text-decoration: none;
  background: ${({ theme }) => theme.colors.backgroundCard};
  border-radius: 12px;
  padding: 18px 20px;
  border: 1.5px solid var(--clr-surface-border);
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    border-color: var(--clr-accent-border-h);
    box-shadow: ${({ theme }) => theme.shadows.hover};
    text-decoration: none;
  }
`;

const CertificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const LogoWrapper = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--clr-surface-bg);
  border-radius: 8px;
  border: 1px solid var(--clr-surface-border);
  padding: 6px;
  transition: all 0.3s ease;

  ${CertificationCard}:hover & {
    background: var(--clr-accent-fill);
    border-color: var(--clr-accent-border);
  }
`;

const CertificationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CertificationLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const PDFPreview = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 11px;
  text-align: center;
  line-height: 1.1;
`;

const CertificationTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const CertificationOrg = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.accent};
  margin: 0 0 3px 0;
  font-weight: 500;
`;

const CertificationDate = styled.p`
  font-size: 11px;
  color: var(--clr-text-faint);
  margin: 0 0 10px 0;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillTag = styled.span`
  background: var(--clr-accent-fill);
  color: var(--clr-text-dim);
  border: 1px solid var(--clr-accent-border);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const CredentialLink = styled.a`
  display: inline-block;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Certifications: React.FC = () => {
  const { t } = useTranslation();
  // Grouper les certifications par organisation
  const certificationGroups = {
    Microsoft: portfolioData.certifications.filter(cert => cert.organization === 'Microsoft'),
    AWS: portfolioData.certifications.filter(cert => cert.organization === 'Amazon Web Services (AWS)'),
    'Cloud & DevOps': portfolioData.certifications.filter(cert => cert.organization === 'KodeKloud'),
    IBM: portfolioData.certifications.filter(cert => cert.organization === 'IBM')
  };

  const renderCertificationCard = (cert: any) => (
    <CertificationCard
      key={cert.id}
      variants={itemVariants}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      href={cert.credentialUrl || '#'}
      target={cert.credentialUrl ? "_blank" : undefined}
      rel={cert.credentialUrl ? "noopener noreferrer" : undefined}
      onClick={!cert.credentialUrl ? (e: any) => e.preventDefault() : undefined}
    >
      <CertificationHeader>
        {cert.logo && (
          <LogoWrapper>
            {cert.logo.endsWith('.pdf') ? (
              <PDFPreview title="View Certificate">
                {cert.organization === 'KodeKloud' ? 'KK' : 'PDF'}
              </PDFPreview>
            ) : (
              <CertificationLogo
                src={cert.logo}
                alt={`${cert.organization} certification logo`}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
            )}
          </LogoWrapper>
        )}
        <CertificationInfo>
          <CertificationTitle>{cert.title}</CertificationTitle>
          <CertificationOrg>{cert.organization}</CertificationOrg>
          <CertificationDate>
            Issued: {cert.issueDate}
            {cert.expirationDate && ` • Expires: ${cert.expirationDate}`}
          </CertificationDate>
          <SkillsList>
            {cert.skills.map((skill: string, index: number) => (
              <SkillTag key={index}>{skill}</SkillTag>
            ))}
          </SkillsList>
          {cert.credentialUrl && (
            <CredentialLink
              href={cert.credentialUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: any) => e.stopPropagation()}
            >
              {cert.credentialUrl.endsWith('.pdf') ? 'View Certificate (PDF) →' : 'View Credential →'}
            </CredentialLink>
          )}
          {cert.credentialId && !cert.credentialUrl && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--clr-text-faint)' }}>
              ID: {cert.credentialId}
            </div>
          )}
        </CertificationInfo>
      </CertificationHeader>
    </CertificationCard>
  );

  return (
    <CertificationsContainer>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {t('certifications.sectionTitle')}
        </SectionTitle>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {Object.entries(certificationGroups).map(([category, certs]) => (
            certs.length > 0 && (
              <CategorySection key={category}>
                <CategoryTitle
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  {t(`certifications.categories.${category}`, category)}
                </CategoryTitle>
                <CertificationGrid>
                  {certs.map(renderCertificationCard)}
                </CertificationGrid>
              </CategorySection>
            )
          ))}
        </motion.div>
      </Container>
    </CertificationsContainer>
  );
};

export default Certifications;